import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Target, Plus, Check, Trash2, Sparkles } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { useQuarterlyGoals } from '../../../hooks/useQuarterlyGoals'
import { cn } from '../../../lib/utils'

// Quarterly goals section rendered at the top of /phases. 3-5 goals
// per quarter + the latest Saturday AI review (written by the
// quarterly-goals-review cron).

export default function QuarterlyGoals() {
  const { t } = useTranslation()
  const { quarter, goals, latestReview, loading, add, setStatus, remove } = useQuarterlyGoals()
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [metric, setMetric] = useState('')
  const [busy, setBusy] = useState(false)

  if (loading) return null

  const active = goals.filter((g) => g.status === 'active')
  const done = goals.filter((g) => g.status === 'done')

  const submit = async (e) => {
    e.preventDefault()
    if (!title.trim() || busy) return
    setBusy(true)
    const ok = await add(title, metric)
    setBusy(false)
    if (ok) { setTitle(''); setMetric(''); setAdding(false) }
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold flex items-center gap-1.5 truncate" dir="auto">
            <Target className="w-4 h-4 text-app-primary-500" />
            {t('goals.title')}
            <span className="text-xs font-normal text-muted-foreground" dir="ltr">{quarter}</span>
          </h2>
          {!adding && goals.length < 5 && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4 me-1" />
              {t('goals.add')}
            </Button>
          )}
        </div>

        {goals.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground" dir="auto">{t('goals.empty')}</p>
        )}

        {adding && (
          <form onSubmit={submit} className="space-y-2">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('goals.titlePlaceholder')}
              maxLength={120}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
              dir="auto"
            />
            <input
              type="text"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              placeholder={t('goals.metricPlaceholder')}
              maxLength={80}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
              dir="auto"
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || !title.trim()} className="flex-1">
                {t('common.save')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}

        {(active.length > 0 || done.length > 0) && (
          <div className="divide-y divide-border/60">
            {[...active, ...done].map((g) => (
              <GoalRow key={g.id} goal={g} onStatus={setStatus} onRemove={remove} t={t} />
            ))}
          </div>
        )}

        {latestReview && (
          <div className="rounded-md border border-app-primary-500/30 bg-app-primary-500/5 p-3 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500 flex items-center gap-1" dir="auto">
              <Sparkles className="w-3.5 h-3.5" />
              {t('goals.reviewTitle')}
              <span className="font-normal normal-case text-muted-foreground ms-auto" dir="ltr">{latestReview.week_start}</span>
            </p>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" dir="auto">
              {latestReview.review_text}
            </p>
          </div>
        )}
        {!latestReview && active.length > 0 && (
          <p className="text-[11px] text-muted-foreground" dir="auto">{t('goals.reviewPending')}</p>
        )}
      </CardContent>
    </Card>
  )
}

function GoalRow({ goal, onStatus, onRemove, t }) {
  const [confirming, setConfirming] = useState(false)
  const isDone = goal.status === 'done'
  return (
    <div className="py-2.5 flex items-center gap-2.5">
      <button
        type="button"
        onClick={() => onStatus(goal.id, isDone ? 'active' : 'done')}
        aria-label={isDone ? t('goals.reopen') : t('goals.markDone')}
        className={cn(
          'w-6 h-6 rounded-full border-2 inline-flex items-center justify-center flex-shrink-0 transition-colors',
          isDone
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-muted-foreground/40 text-transparent hover:border-app-primary-500',
        )}
      >
        {isDone && <Check className="w-3.5 h-3.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium truncate', isDone ? 'text-muted-foreground line-through' : 'text-foreground')} dir="auto">
          {goal.title}
        </p>
        {goal.metric && (
          <p className="text-xs text-muted-foreground truncate" dir="auto">{goal.metric}</p>
        )}
      </div>
      {confirming ? (
        <Button size="sm" variant="destructive" onClick={() => onRemove(goal.id)} onBlur={() => setConfirming(false)}>
          {t('goals.confirm')}
        </Button>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label={t('common.delete')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground flex-shrink-0"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
